import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { t } from 'i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
// eslint-disable-next-line unused-imports/no-unused-imports, no-unused-vars
import { OrgUser } from '@/models/User/OrgUser';
import LocationSharingGroupItem from './LocationSharingGroupItem';
import {
  getUserLocation,
  getUserStatus,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import {
  useVisibleGroups,
  useLocationSharingActions,
} from '@/stores/map/locationSharingStore';
import { useMapUiActions } from '@/stores/map/mapUiStore';

const theme = {
  background: '#ffffff',
  headerBackground: '#f5f8ff',
  text: '#333333',
  mutedText: 'rgba(0, 0, 0, 0.6)',
  border: 'rgba(0, 0, 0, 0.1)',
  hoverBackground: 'rgba(0, 0, 0, 0.04)',
  inputBackground: '#f9f9f9',
  inputBorder: 'rgba(0, 0, 0, 0.15)',
  inputHoverBorder: 'rgba(0, 0, 0, 0.3)',
  divider: 'rgba(0, 0, 0, 0.08)',
  itemBackground: 'rgba(0, 0, 0, 0.02)',
  expandedGroupBg: 'rgba(0, 0, 0, 0.03)',
  badgeBackground: 'rgba(0, 0, 0, 0.05)',
  scrollThumb: 'rgba(0, 0, 0, 0.2)',
  scrollTrack: 'rgba(0, 0, 0, 0.05)',
  accent: '#4285f4',
  disabledIcon: '#b4b4b4',
};

/**
 * LocationSharingPanel component displays a list of user groups with their members,
 * allowing users to toggle visibility of groups, search for users,
 * and click on users to view their locations.
 *
 * @returns {JSX.Element}
 */
const LocationSharingPanel = () => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  /**
   * @type {[Object.<string, OrgUser[]>, Function]}
   */
  const [usersByGroup, setUsersByGroup] = useState({});
  const [loading, setLoading] = useState(true);
  const { groups } = useOrganizationUpdaterProvider();

  // Store hooks
  const visibleGroups = useVisibleGroups();
  const locationSharingActions = useLocationSharingActions();

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  const handleToggleGroup = (groupId) => {
    locationSharingActions.handleGroupToggle(groupId);
  };

  useEffect(() => {
    if (groups && Array.isArray(groups)) {
      try {
        const usersMap = {};
        groups.forEach((group) => {
          if (group.group_value) usersMap[group.group_name] = group.group_value;
        });
        setUsersByGroup(usersMap);
      } catch (error) {
        console.error('Error processing groups:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [groups]);

  // memoize the filtered groups to avoid unnecessary recalculations
  const filteredGroups = useMemo(() => {
    return (Array.isArray(groups) ? groups : []).filter((group) => {
      if (!group) return false;
      if (searchTerm) {
        if (group.group_name?.toLowerCase().includes(searchTerm.toLowerCase()))
          return true;
        const groupUsers = usersByGroup[group.group_name] || [];
        return groupUsers.some((user) => {
          if (!user) return false;
          const fullName =
            `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
          return (
            fullName.includes(searchTerm.toLowerCase()) ||
            (user.phone_number || '').includes(searchTerm)
          );
        });
      }
      return true;
    });
  }, [groups, searchTerm, usersByGroup]);

  return (
    <Paper
      elevation={3}
      sx={{
        height: '100%',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        color: theme.text,
        borderRight: `1px solid ${theme.border}`,
      }}
    >
      {/* Search box */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.border}`,
          backgroundColor: theme.headerBackground,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={t(StringKeys.SEARCH_USERS)}
          value={searchTerm}
          onChange={handleSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.mutedText }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <Clear
                    sx={{
                      color: theme.mutedText,
                      cursor: 'pointer',
                    }}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ) : null,
              sx: {
                backgroundColor: theme.inputBackground,
                color: theme.text,
                borderRadius: '4px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.inputBorder,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.inputHoverBorder,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.accent,
                },
                '& input::placeholder': {
                  color: theme.mutedText,
                  opacity: 1,
                },
              },
            },
          }}
          sx={{
            '& label.Mui-focused': {
              color: theme.accent,
            },
          }}
        />
      </Box>

      {/* Team list */}
      <List
        sx={{
          overflowY: 'auto',
          flexGrow: 1,
          p: 0,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.scrollTrack,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.scrollThumb,
            borderRadius: '4px',
          },
        }}
      >
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.mutedText }}>
              {t(StringKeys.LOADING)}
            </Typography>
          </Box>
        ) : (
          filteredGroups.map((group) => {
            const isExpanded = expandedGroups[group.group_name];
            const isVisible = visibleGroups.includes(group.group_name);
            const groupUsers = usersByGroup[group.group_name] || [];

            return (
              <LocationSharingGroupItem
                key={group.group_name}
                group={group}
                handleToggleGroup={handleToggleGroup}
                isExpanded={isExpanded}
                isVisible={isVisible}
                setExpandedGroups={setExpandedGroups}
                users={groupUsers}
                theme={theme}
                searchTerm={searchTerm}
              />
            );
          })
        )}

        {!loading && filteredGroups.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.mutedText }}>
              {t(StringKeys.NO_USERS_MATCH_YOUR_SEARCH)}
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  );
};

export default LocationSharingPanel;
